import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6djopqtehdqnvnpive0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-[#F5F1E8]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Start Your Journey
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Take the first step toward healing and personal growth. Reach out today to schedule your consultation.
          </p>
        </div>

        <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl p-10 shadow-lg">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7C] text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7C] text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7C] text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
            <div>
              <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Service Interested In *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7C] text-sm cursor-pointer"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">Select a service</option>
                <option value="Individual Therapy">Individual Therapy</option>
                <option value="Couples Therapy">Couples Therapy</option>
                <option value="Group Sessions">Group Sessions</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7C] text-sm resize-none"
              style={{ fontFamily: 'Inter, sans-serif' }}
              placeholder="Tell us a bit about what brings you here..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              {formData.message.length}/500 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#2C5F7C] text-white py-4 rounded-full text-base font-semibold hover:bg-[#1e4456] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
          </button>

          {submitStatus === 'success' && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                Thank you for reaching out! We'll get back to you within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                Something went wrong. Please try again or call us directly.
              </p>
            </div>
          )}
        </form>

        <div className="mt-16 flex items-center justify-center gap-8">
          <div className="w-20 h-20 flex items-center justify-center">
            <i className="ri-lighthouse-line text-5xl text-[#2C5F7C] opacity-30"></i>
          </div>
          <div className="w-20 h-20 flex items-center justify-center">
            <i className="ri-sailboat-line text-5xl text-[#2C5F7C] opacity-30"></i>
          </div>
          <div className="w-20 h-20 flex items-center justify-center">
            <i className="ri-water-flash-line text-5xl text-[#2C5F7C] opacity-30"></i>
          </div>
        </div>
      </div>
    </section>
  );
}
